import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { usersAPI } from "../services/api";

const ProfileSettingsModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    full_name: "",
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        full_name: user.full_name || "",
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
      setError("");
      setSuccess("");
    }
  }, [user, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    // Validate form
    if (!formData.full_name.trim()) {
      setError("Full name is required");
      setLoading(false);
      return;
    }

    // If changing password, validate password fields
    if (formData.new_password) {
      if (!formData.current_password) {
        setError("Current password is required to set a new password");
        setLoading(false);
        return;
      }

      if (formData.new_password.length < 6) {
        setError("New password must be at least 6 characters long");
        setLoading(false);
        return;
      }

      if (formData.new_password !== formData.confirm_password) {
        setError("New passwords do not match");
        setLoading(false);
        return;
      }
    }

    try {
      const updateData = {
        full_name: formData.full_name,
      };

      // Only include password if user is changing it
      if (formData.new_password) {
        updateData.password = formData.new_password;
      }

      await usersAPI.update(user.id, updateData);

      setSuccess("Profile updated successfully!");

      // Update local user data
      const updatedUser = { ...user, full_name: formData.full_name };
      localStorage.setItem("user", JSON.stringify(updatedUser));

      // Clear password fields
      setFormData({
        ...formData,
        current_password: "",
        new_password: "",
        confirm_password: "",
      });

      setTimeout(() => {
        setSuccess("");
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Profile Settings
                    </h3>
                    <button
                      type="button"
                      onClick={onClose}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  {error && (
                    <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                      {error}
                    </div>
                  )}

                  {success && (
                    <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg text-sm">
                      {success}
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* User Info */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center">
                          <span className="text-lg font-semibold text-white">
                            {user?.full_name?.charAt(0) || "U"}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {user?.full_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            @{user?.username}
                          </div>
                          <div className="text-xs text-gray-500 capitalize">
                            {user?.role}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Profile Form */}
                    <div>
                      <label htmlFor="full_name" className="form-label">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="full_name"
                        name="full_name"
                        required
                        className="form-input"
                        placeholder="Enter your full name"
                        value={formData.full_name}
                        onChange={handleChange}
                        disabled={loading}
                      />
                    </div>

                    <div className="border-t pt-4">
                      <h4 className="font-medium text-gray-900 mb-3">
                        Change Password
                      </h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Leave blank to keep current password
                      </p>

                      <div className="space-y-4">
                        <div>
                          <label
                            htmlFor="current_password"
                            className="form-label"
                          >
                            Current Password
                          </label>
                          <input
                            type="password"
                            id="current_password"
                            name="current_password"
                            className="form-input"
                            placeholder="Enter current password"
                            value={formData.current_password}
                            onChange={handleChange}
                            disabled={loading}
                          />
                        </div>

                        <div>
                          <label htmlFor="new_password" className="form-label">
                            New Password
                          </label>
                          <input
                            type="password"
                            id="new_password"
                            name="new_password"
                            className="form-input"
                            placeholder="Enter new password"
                            value={formData.new_password}
                            onChange={handleChange}
                            disabled={loading}
                          />
                          {formData.new_password &&
                            formData.new_password.length < 6 && (
                              <p className="mt-1 text-xs text-red-500">
                                Password must be at least 6 characters long
                              </p>
                            )}
                        </div>

                        <div>
                          <label
                            htmlFor="confirm_password"
                            className="form-label"
                          >
                            Confirm New Password
                          </label>
                          <input
                            type="password"
                            id="confirm_password"
                            name="confirm_password"
                            className="form-input"
                            placeholder="Confirm new password"
                            value={formData.confirm_password}
                            onChange={handleChange}
                            disabled={loading}
                          />
                          {formData.confirm_password &&
                            formData.new_password !==
                              formData.confirm_password && (
                              <p className="mt-1 text-xs text-red-500">
                                Passwords do not match
                              </p>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex justify-center btn btn-primary sm:ml-3 sm:w-auto sm:text-sm"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="loading-spinner mr-2"></div>
                    Updating...
                  </div>
                ) : (
                  "Update Profile"
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="mt-3 w-full inline-flex justify-center btn btn-secondary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettingsModal;
